import { OnboardingStepId, OffboardingStepId } from './types';

/**
 * Primary key: patientOnboarding value from questionnaireConfig
 * Value: ordered array of onboarding steps for that variant
 *
 * LANDING STEPS: Each flow uses the specific landing step directly.
 * The transformLandingStep transformer handles skipProfessionalSelection flag,
 * converting landing-professional-select to landing-practice when needed.
 */
export const BASE_FLOWS: Record<string, OnboardingStepId[]> = {
  // LANDING_PAGE → WHY_PREPARE → self-identification → patient info → consultation-time
  DEFAULT: [
    'invitation-authentication',
    'landing-professional-select',
    'why-prepare',
    'self-identification',
    'patient-information',
    'patient-administrative',
    'patient-contact',
    'patient-address',
    'patient-profession',
    'consultation-time',
  ],

  // LANDING_PAGE → WHY_PREPARE → self-identification → patient info → steps-overview
  MED_G: [
    'invitation-authentication',
    'landing-professional-select',
    'why-prepare',
    'self-identification',
    'patient-information',
    'patient-administrative',
    'patient-contact',
    'patient-address',
    'patient-profession',
    'consultation-time',
    'steps-overview',
  ],

  // SNP_DEMO — identical to MED_G. Used for referrer=site flow.
  // Entry sets variant=snp-demo → offboarding uses CONFIRMATION_ONLY (skips research-consent).
  SNP_DEMO: [
    'invitation-authentication',
    'landing-professional-select',
    'why-prepare',
    'self-identification',
    'patient-information',
    'patient-administrative',
    'patient-contact',
    'patient-address',
    'patient-profession',
    'consultation-time',
    'steps-overview',
  ],

  // NavigationProvider lines 229-231, 235-236, 303-312:
  // LANDING_PAGE → WHY_PREPARE → INTRO_1 → INTRO_2 → patient info
  SOMMEIL: [
    'invitation-authentication',
    'landing-professional-select',
    'why-prepare',
    'sommeil-intro-1',
    'sommeil-intro-2',
    'self-identification',
    'patient-information',
    'patient-administrative',
    'patient-contact',
    'patient-address',
    'patient-profession',
    'consultation-time',
  ],

  SOMMEIL_BLIGNY: [
    'landing-sommeil-bligny',
    'invitation-authentication',
    'sommeil-intro-1',
    'sommeil-intro-2',
    'sommeil-bligny-recherche',
    'self-identification',
    'patient-information',
    'patient-administrative',
    'patient-contact',
    'patient-address',
    'patient-profession',
    'consultation-time',
  ],

  MON_BILAN_PREVENTION: [
    'invitation-authentication',
    'landing-professional-select',
    'mbp-intro-1',
    'mbp-intro-2',
    'self-identification',
    'patient-information',
    'patient-administrative',
    'patient-contact',
    'patient-address',
    'patient-profession',
    'consultation-time',
  ],

  ETP_SOMMEIL: [
    'landing-etp-sommeil',
    'invitation-authentication',
    'self-identification',
    'patient-information',
    'patient-administrative',
    'patient-contact',
    'patient-address',
    'patient-profession',
    'consultation-time',
  ],

  // The borne (mmg_borne kiosk) is always the patient checking themselves in,
  // so we skip the "Pour qui préparez-vous cette consultation ?" step and
  // assume SELF (ALDO-2540). `computeSelf` already defaults to SELF for adults
  // when self-identification is absent, and `PatientInformation` injects the
  // interlocuter regardless, so no extra dispatch is needed here.
  BORNE: [
    'landing-practice',
    'patient-information',
    'patient-administrative',
    'patient-contact',
    'patient-address',
    'patient-profession',
  ],

  // PROM ARS — questionnaire de suivi post-consultation (KCCQ-12). Le patient
  // ne prépare pas un rendez-vous, il mesure son vécu :
  //  - accueil dédié `landing-prom-ars` (LandingPromArs) plutôt que
  //    `landing-professional-select`, dont le titre, le carrousel de sélection
  //    et le CTA ne s'appliquent pas ici ;
  //  - « why-prepare » (« Une consultation mieux préparée, ça change tout »)
  //    est hors sujet ;
  //  - « consultation-time » aussi, puisqu'il n'y a pas de rendez-vous à dater.
  //
  // Conséquence côté KB : #CONSULTATION_DATE et #CONSULTATION_TIME restent
  // sans réponse, donc l'âge du patient n'est pas calculé. Sans effet sur le
  // KCCQ-12, dont les libellés ne dépendent que du Locuteur et du genre.
  PROM_ARS: [
    'invitation-authentication',
    'landing-prom-ars',
    'self-identification',
    'patient-information',
    'patient-administrative',
    'patient-contact',
    'patient-address',
    'patient-profession',
  ],
};

/**
 * Key: patientOffboarding value from questionnaireConfig
 * Value: ordered array of offboarding steps
 * Verified against NavigationProvider.tsx lines 155-173
 */
export const OFFBOARDING_FLOWS: Record<string, OffboardingStepId[]> = {
  // Lines 159-170: questionnaire last page → research-consent → confirmation
  DEFAULT: ['research-consent', 'confirmation'],

  // Line 172: questionnaire last page → direct-confirmation
  DIRECT_CONFIRMATION: ['direct-confirmation'],

  // NEW: For snp-demo variant (referrer=site). Skips research-consent,
  // goes directly to regular /confirmation page.
  // Previously handled by NavigationProvider lines 160-163 (referrer === 'site' check).
  CONFIRMATION_ONLY: ['confirmation'],
};
